import {Firmware} from "../firmware";
import {Organization} from "../organization";

export enum DeviceField {
    ID = "id",
    ICCID = "iccid",
    IMEI = "imei",
    MSISDN = "msisdn",
    IMSI = "imsi",
    ONLINE = "online",
    LICENSE_PLATE = "licensePlate",
    HARDWARE_TYPE = "hardwareType",
    SERIAL_NUMBER = "serialNumber",
    CURRENT_FIRMWARE = "currentFirmware",
    CURRENT_FIRMWARE_ID = "currentFirmwareId",
    CURRENT_FIRMWARE_VERSION_NUMBER = "currentFirmwareVersionNumber",
    CURRENT_FIRMWARE_LEGACY = "currentFirmwareLegacy",
    TARGET_FIRMWARE = "targetFirmware",
    TARGET_FIRMWARE_ID = "targetFirmwareId",
    TARGET_FIRMWARE_VERSION_NUMBER = "targetFirmwareVersionNumber",
    TARGET_FIRMWARE_LEGACY = "targetFirmwareLegacy",
    FIRMWARE_UPDATE_STATUS = "firmwareUpdateStatus",
    FIRMWARE_UPDATE_SCHEDULED_AT = "firmwareUpdateScheduledAt",
    FIRMWARE_UPDATE_PROGRESS = "firmwareUpdateProgress",
    FIRMWARE_UPDATE_TRY_COUNT = "firmwareUpdateTryCount",
    FIRMWARE_UPDATE_ERROR = "firmwareUpdateError",
    ORGANIZATION = "organization",
    ORGANIZATION_ID = "organizationId",
    ORGANIZATION_NAME = "organizationName",
    ORGANIZATION_CUSTOMER_NUMBER = "organizationCustomerNumber",
    TENANT = "tenant",
    LAST_CONNECTION_AT = "lastConnectionAt",
    CREATED_AT = "createdAt",
    UPDATED_AT = "updatedAt",
    CAPABILITIES = "capabilities",
    RDL_STATUS = "rdlStatus",
    SYSTEM_INFORMATION = "systemInformation",
    TACHO_INFORMATION = "tachoInformation",
    LEO_ID = "leoId",
    PREDECESSOR_LEO_ID = "predecessorLeoId",
    DRIVERCARDS = "driverCards",
    SLOT1_DRIVER_CARD_PRESENT = "slot1DriverCardPresent",
    SLOT2_DRIVER_CARD_PRESENT = "slot2DriverCardPresent",
    PL_SENT_ID = "plSentId",
    PL_SENT_STATUS = "plSentStatus",
}

export enum DeviceCapabilityField {
    NAME = "name",
    STATUS = "status",
    HINT = "hint",
}

export enum DeviceDriverCardField {
    DRIVERCARD_ID = "driverCardId",
    SLOT = "slot",
}

export enum PlSentStatus {
    PENDING = "PENDING",
    UNREGISTERED = "UNREGISTERED",
    REGISTERED = "REGISTERED",
    FAILED = "FAILED",
}

export interface Device {
    [DeviceField.ID]: number;
    [DeviceField.CREATED_AT]: string;
    [DeviceField.CURRENT_FIRMWARE]?: Firmware;
    [DeviceField.TARGET_FIRMWARE]?: Firmware;
    [DeviceField.FIRMWARE_UPDATE_ERROR]: string;
    [DeviceField.FIRMWARE_UPDATE_PROGRESS]: number;
    [DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT]: string;
    [DeviceField.FIRMWARE_UPDATE_STATUS]: FirmwareUpdateStatus;
    [DeviceField.FIRMWARE_UPDATE_TRY_COUNT]: number;
    [DeviceField.HARDWARE_TYPE]: string;
    [DeviceField.ICCID]: string;
    [DeviceField.IMEI]: string;
    [DeviceField.IMSI]: string;
    [DeviceField.MSISDN]: string;
    [DeviceField.LICENSE_PLATE]: string;
    [DeviceField.ONLINE]: boolean;
    [DeviceField.LAST_CONNECTION_AT]: string;
    [DeviceField.ORGANIZATION]?: Organization;
    [DeviceField.SERIAL_NUMBER]: string;
    [DeviceField.TENANT]: string;
    [DeviceField.UPDATED_AT]: string;
    [DeviceField.CAPABILITIES]: DeviceCapability[];
    [DeviceField.RDL_STATUS]: DeviceCapabilityStatus;
    [DeviceField.SYSTEM_INFORMATION]?: DeviceSystemInformation;
    [DeviceField.TACHO_INFORMATION]?: TachoInformation;
    [DeviceField.LEO_ID]: string;
    [DeviceField.PREDECESSOR_LEO_ID]?: string;
    [DeviceField.DRIVERCARDS]?: DeviceDriverCard[];
    [DeviceField.SLOT1_DRIVER_CARD_PRESENT]: boolean;
    [DeviceField.SLOT2_DRIVER_CARD_PRESENT]: boolean;
    [DeviceField.PL_SENT_ID]?: string;
    [DeviceField.PL_SENT_STATUS]: PlSentStatus;
}

export enum DeviceCapability {
    GPS = "GPS",
    FMS = "FMS",
    DTCO = "DTCO",
    RDL = "RDL",
}

export interface DeviceDriverCard {
    [DeviceDriverCardField.DRIVERCARD_ID]: string;
    [DeviceDriverCardField.SLOT]: number;
}

export enum DeviceCapabilityStatus {
    AVAILABLE = "available",
    OFFLINE = "offline",
    ERROR = "error",
    UNSUPPORTED = "unsupported",
}

export interface DeviceSystemInformation {
    connectionType: string;
    fmsVersion: string;
    canClickModule: string;
    canClickFirmware: string;
    lpwaFirmwareVersion: string;
}

export interface TachoInformation {
    authenticationStatus: boolean;
    licensePlate: string;
    registeringMemberState: string;
    vin: string;
    brand?: string;
    hardwarePartNumber?: string;
    hardwareVersion?: string;
    softwarePartNumber?: string;
    softwareVersion?: string;
}

export interface DeviceFlat
    extends Pick<
        Device,
        | DeviceField.ID
        | DeviceField.CREATED_AT
        | DeviceField.FIRMWARE_UPDATE_ERROR
        | DeviceField.FIRMWARE_UPDATE_PROGRESS
        | DeviceField.FIRMWARE_UPDATE_SCHEDULED_AT
        | DeviceField.FIRMWARE_UPDATE_STATUS
        | DeviceField.FIRMWARE_UPDATE_TRY_COUNT
        | DeviceField.HARDWARE_TYPE
        | DeviceField.ICCID
        | DeviceField.IMEI
        | DeviceField.IMSI
        | DeviceField.MSISDN
        | DeviceField.LICENSE_PLATE
        | DeviceField.ONLINE
        | DeviceField.LAST_CONNECTION_AT
        | DeviceField.SERIAL_NUMBER
        | DeviceField.TENANT
        | DeviceField.UPDATED_AT
        | DeviceField.RDL_STATUS
        | DeviceField.LEO_ID
        | DeviceField.PL_SENT_ID
        | DeviceField.PL_SENT_STATUS
    > {
    [DeviceField.CAPABILITIES]?: DeviceCapability[];
    [DeviceField.CURRENT_FIRMWARE_ID]?: number;
    [DeviceField.CURRENT_FIRMWARE_VERSION_NUMBER]?: string;
    [DeviceField.CURRENT_FIRMWARE_LEGACY]?: boolean;
    [DeviceField.TARGET_FIRMWARE_ID]?: number;
    [DeviceField.TARGET_FIRMWARE_VERSION_NUMBER]?: string;
    [DeviceField.TARGET_FIRMWARE_LEGACY]?: boolean;
    [DeviceField.ORGANIZATION_ID]?: number;
    [DeviceField.ORGANIZATION_NAME]?: string;
    [DeviceField.ORGANIZATION_CUSTOMER_NUMBER]?: string;
}

export enum FirmwareUpdateStatus {
    UP_TO_DATE = 0,
    SCHEDULED = 1,
    IN_PROGRESS = 2,
    ERROR = 3,
    UNKNOWN = 4,
    WAITING_FOR_CHECKIN = 5,
    CANCELED = 6,
    WAITING_FOR_RETRY = 7,
}

export enum DeviceJobField {
    DEVICE_ID = "deviceId",
    TYPE = "type",
    STAGE = "stage",
    PERIOD_START = "periodStart",
    PERIOD_END = "periodEnd",
    DRIVER_CARD_NUMBER = "driverCardNumber",
    DRIVER_CARD_SLOT = "driverCardSlot",
    DRIVER_CARD_ID = "driverCardId",
}
